import { SignedInHeader } from "@/auth/SignedInHeader";
import { AgentRequestsBell } from "@/features/agentrequests/AgentRequestsBell";
import { ConnectionsBell } from "@/features/connections/ConnectionsBell";
import {
  BookingIcon,
  CalendarIcon,
  HomeIcon,
  IdCardIcon,
  NavIconLink,
  ShieldIcon,
  UserIcon,
} from "@/features/home/components/NavIcons";
import { useProfileOptional } from "@/features/profile/ProfileContext";
import { useFeature } from "@/features/profile/useFeature";

import { BotMessageSquare } from "lucide-react";

import { DelgenticHomeLink } from "./DelgenticHomeLink";
import { SchedaLogo } from "./SchedaLogo";

/**
 * The shared top nav for authenticated pages: the Scheda brand (links home) plus
 * the same primary icon links + signed-in header the ChatPage shows. Gives every
 * page one consistent way around the app — and, on inner pages, a way back home.
 *
 * Reads the profile via {@link useProfileOptional} so it also tolerates the public
 * agent-card page, which lives outside ProfileProvider: with no profile it simply
 * drops the handle-specific "My card" and the admin link.
 */
/**
 * Chat lives on its own page now; Home is the overview. A bot-in-a-bubble rather
 * than a plain message icon — the nav should say "talk to your agent", not "messages".
 */
function ChatIcon() {
  return <BotMessageSquare className="h-5 w-5" />;
}

export function AppNav() {
  const state = useProfileOptional()?.state;
  const isAdmin = state?.status === "ready" && state.profile.admin;
  const handle = state?.status === "ready" ? state.profile.handle : null;
  // Chat used to be hidden alongside connections, on the grounds that offering someone a chat box
  // which answers every message with an upsell is worse than not offering it. That reasoning is
  // now satisfied rather than broken: the chat refuses nobody. Asking is open to everyone, and
  // hosting is what Pro buys (D-48, D-51), so a Free member gets a working page that simply does
  // less. Connections and the agent card stay on AI_SCHEDULING, which is why they no longer move
  // together and the comment that said they did has gone.
  const connections = useFeature("AI_SCHEDULING");
  const showConnections = isAdmin || connections === "granted";

  return (
    <header className="sticky top-0 z-30 border-b-2 border-black bg-background/90 backdrop-blur">
      {/* Full-width, three zones: brand left, primary menu CENTERED, account
          actions right. The centre column is auto-width between two equal 1fr
          rails, so the menu stays optically centred on the page regardless of how
          wide the brand or the signed-in header get. */}
      <div className="mx-auto grid max-w-none grid-cols-[1fr_auto_1fr] items-center gap-3 px-6 py-2">
        <div className="flex items-center gap-2 justify-self-start">
          <SchedaLogo />
          <DelgenticHomeLink />
        </div>
        <nav className="flex items-center gap-0.5 justify-self-center sm:gap-1">
          {/* Icons rotate through the landing palette (blue / teal / coral); admin uses the
              contrast red. The active page is marked by a brand-contrast underline (NavIconLink). */}
          <NavIconLink to="/" label="Home" color="text-brand-blue">
            <HomeIcon />
          </NavIconLink>
          {/* Every member, whatever their tier. The page works for all of them; what Pro buys is
              being reachable BY other people's agents, not the ability to ask (D-48). */}
          <NavIconLink to="/chat" label="Agent chat" color="text-brand-coral">
            <ChatIcon />
          </NavIconLink>
          <NavIconLink to="/meetings" label="Meetings" color="text-brand-teal">
            <CalendarIcon />
          </NavIconLink>
          <NavIconLink to="/booking-pages" label="Booking pages" color="text-brand-blue">
            <BookingIcon />
          </NavIconLink>
          {/* Shown to everyone, unlike the chat icon. Someone with a pending request needs to know
              it is there, and someone carrying a connection they never agreed to needs the way in
              to remove it — neither can depend on holding the paid capability. */}
          <ConnectionsBell />
          {/* Only where an agent can actually reach this host. The bell hides itself rather than
              sitting at zero forever for somebody nobody can ask. */}
          <AgentRequestsBell />
          <NavIconLink to="/profile" label="Profile" color="text-brand-coral">
            <UserIcon />
          </NavIconLink>
          {/* The card IS the A2A face, and without AI_SCHEDULING the owner's own /@handle answers
              with the empty "No agent here" panel. Linking to it would be sending someone to look
              at their own absence. This no longer moves with the chat icon, which every member
              now sees. */}
          {handle && showConnections && (
            <NavIconLink to={`/@${handle}`} label="My card" color="text-brand-teal">
              <IdCardIcon />
            </NavIconLink>
          )}
          {isAdmin && (
            <NavIconLink to="/admin" label="Admin" color="text-brand-contrast">
              <ShieldIcon />
            </NavIconLink>
          )}
        </nav>
        <div className="flex items-center gap-2 justify-self-end">
          <SignedInHeader />
        </div>
      </div>
    </header>
  );
}
